import type {
  TicketTypeTemplate,
  TicketTypeTemplateCatalog,
  TicketTypeTemplateOverride,
} from '../types/ticketTypeTemplate';

const API_BASE_URL = '/api';

// ---------------------------------------------------------------------------
// Global catalog
// ---------------------------------------------------------------------------

/**
 * Fetch the ticket-type-templates catalog. When `customerId` is supplied,
 * the API merges that customer's active overrides into matching templates.
 */
export async function getTicketTypeTemplates(
  signalOrOptions?: AbortSignal | { customerId?: string; signal?: AbortSignal },
): Promise<TicketTypeTemplateCatalog> {
  const opts =
    signalOrOptions instanceof AbortSignal
      ? { signal: signalOrOptions }
      : signalOrOptions ?? {};
  const params = new URLSearchParams();
  if ('customerId' in opts && opts.customerId) {
    params.set('customerId', opts.customerId);
  }
  const qs = params.toString();
  const url = `${API_BASE_URL}/ticket-type-templates${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal: opts.signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch ticket type templates: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

/**
 * Save catalog edits via per-template POST/PATCH/DELETE. The legacy
 * bulk `PUT /api/ticket-type-templates` is gone (returns 410); this
 * function diffs the incoming catalog against the live server state
 * and applies the minimal set of writes so the existing GUI editor
 * keeps working without changes.
 */
export async function putTicketTypeTemplates(
  catalog: TicketTypeTemplateCatalog,
  signal?: AbortSignal,
): Promise<TicketTypeTemplateCatalog> {
  const live = await getTicketTypeTemplates({ signal });
  const liveById = new Map<string, TicketTypeTemplate>();
  for (const t of live.templates) {
    if (t.ticketTypeId) liveById.set(t.ticketTypeId, t);
  }

  // A template raised as another's follow-up must exist before the template
  // naming it is saved — sm4rt-api refuses a follow-up whose target isn't an
  // active template. So follow-up targets go first; otherwise catalog order.
  const targets = new Set(
    catalog.templates.flatMap((t) => (t.followUpTodos ?? []).map((f) => f.ticketTypeId)),
  );
  const ordered = [
    ...catalog.templates.filter((t) => targets.has(t.ticketTypeId)),
    ...catalog.templates.filter((t) => !targets.has(t.ticketTypeId)),
  ];

  const incomingIds = new Set<string>();
  for (const t of ordered) {
    if (!t.ticketTypeId) continue;
    incomingIds.add(t.ticketTypeId);
    const existing = liveById.get(t.ticketTypeId);
    if (!existing) {
      await createTicketTypeTemplate(t, signal);
    } else if (existing.id) {
      // Strip ticketTypeId from the PATCH payload — backend rejects renames.
      const { ticketTypeId: _strip, ...patch } = t;
      await updateTicketTypeTemplate(existing.id, patch, signal);
    }
  }

  // Anything live but missing from `catalog` is a deletion.
  for (const [slug, existing] of liveById.entries()) {
    if (!incomingIds.has(slug) && existing.id) {
      await deleteTicketTypeTemplate(existing.id, signal);
    }
  }

  return getTicketTypeTemplates({ signal });
}

export async function createTicketTypeTemplate(
  template: TicketTypeTemplate,
  signal?: AbortSignal,
): Promise<TicketTypeTemplate> {
  const response = await fetch(`${API_BASE_URL}/ticket-type-templates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(template),
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'create');
  }
  return response.json();
}

export async function updateTicketTypeTemplate(
  templateId: string,
  patch: Partial<TicketTypeTemplate>,
  signal?: AbortSignal,
): Promise<TicketTypeTemplate> {
  const response = await fetch(
    `${API_BASE_URL}/ticket-type-templates/${encodeURIComponent(templateId)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(patch),
      signal,
    },
  );
  if (!response.ok) {
    throw await _toError(response, 'update');
  }
  return response.json();
}

export async function deleteTicketTypeTemplate(
  templateId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/ticket-type-templates/${encodeURIComponent(templateId)}`,
    { method: 'DELETE', signal },
  );
  if (!response.ok) {
    throw await _toError(response, 'delete');
  }
}

export async function clearTicketTypeTemplatesCache(
  signal?: AbortSignal,
): Promise<{ cleared: boolean }> {
  const response = await fetch(
    `${API_BASE_URL}/ticket-type-templates/cache/clear`,
    { method: 'POST', signal },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to clear ticket type templates cache: ${response.status}`,
    );
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Per-customer overrides
// ---------------------------------------------------------------------------

function _overridesUrl(customerId: string, overrideId?: string): string {
  const base = `${API_BASE_URL}/customers/${encodeURIComponent(customerId)}/ticket-type-template-overrides`;
  return overrideId ? `${base}/${encodeURIComponent(overrideId)}` : base;
}

export async function listTicketTypeTemplateOverrides(
  customerId: string,
  signal?: AbortSignal,
): Promise<TicketTypeTemplateOverride[]> {
  const response = await fetch(_overridesUrl(customerId), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'list overrides');
  }
  const body = await response.json();
  return Array.isArray(body.items) ? body.items : [];
}

export async function createTicketTypeTemplateOverride(
  customerId: string,
  payload: {
    ticketTypeId: string;
    templateLineItems?: unknown[] | null;
    additionalFields?: unknown[] | null;
  },
  signal?: AbortSignal,
): Promise<TicketTypeTemplateOverride> {
  const response = await fetch(_overridesUrl(customerId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'create override');
  }
  return response.json();
}

export async function updateTicketTypeTemplateOverride(
  customerId: string,
  overrideId: string,
  patch: {
    templateLineItems?: unknown[] | null;
    additionalFields?: unknown[] | null;
  },
  signal?: AbortSignal,
): Promise<TicketTypeTemplateOverride> {
  const response = await fetch(_overridesUrl(customerId, overrideId), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(patch),
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'update override');
  }
  return response.json();
}

export async function deleteTicketTypeTemplateOverride(
  customerId: string,
  overrideId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(_overridesUrl(customerId, overrideId), {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'delete override');
  }
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

async function _toError(response: Response, action: string): Promise<Error> {
  let message = `Failed to ${action}: ${response.status}`;
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string') {
      message = body.error;
    }
  } catch {
    // ignore
  }
  return new Error(message);
}
